import React, { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Switch,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  FileCopy as FileCopyIcon,
  Edit as EditIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalOffer as LocalOfferIcon,
} from "@material-ui/icons"
import { useHistory, useLocation } from "react-router-dom"
import { paths } from "../../routes/paths"
import styles from "./AdminStripeDiscountCodeListContainer.module.scss"

const validatePercent = (value) => {
  const num = parseInt(String(value).replace(/\D/g, ""), 10)
  return !Number.isNaN(num) && num >= 1 && num <= 100
}

const parsePercentInput = (value) => {
  const digits = String(value).replace(/\D/g, "")
  if (!digits) return ""
  return String(Math.min(parseInt(digits, 10), 100))
}

const sanitizePromoCode = (value) => String(value).toUpperCase().replace(/[^A-Z0-9_-]/g, "")

const PROMO_CODE_PATTERN = /^[A-Z0-9_-]+$/

const formatApiError = (message) => {
  if (!message) return "Something went wrong. Please try again."

  if (message.includes("regex") || message.includes("does not match")) {
    return "Promo codes can only contain letters, numbers, hyphens (-), and underscores (_). Spaces and special characters are not allowed."
  }

  if (message.includes("already exists") || message.toLowerCase().includes("unique")) {
    return "A promotion code with this name already exists."
  }

  return message
}

function AdminStripeDiscountCodeListContainer({
  discountCodes = [],
  loading,
  error,
  onCreate,
  onUpdate,
  onDelete,
}) {
  const history = useHistory()
  const location = useLocation()

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [selectedCode, setSelectedCode] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const [createForm, setCreateForm] = useState({ discount_percent: "", code: "" })
  const [editForm, setEditForm] = useState({ discount_percent: "", active: true })
  const [formError, setFormError] = useState("")
  const [percentTouched, setPercentTouched] = useState(false)
  const [editPercentTouched, setEditPercentTouched] = useState(false)

  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [sortBy, setSortBy] = useState("created_at_desc")

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const status = params.get("status")
    if (status === "active" || status === "inactive") {
      setStatusFilter(status)
    }
  }, [location.search])

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  const filteredCodes = useMemo(() => {
    let result = [...discountCodes]

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toUpperCase()
      result = result.filter((c) => c.code.toUpperCase().includes(query))
    }

    if (statusFilter === "active") {
      result = result.filter((c) => c.active)
    } else if (statusFilter === "inactive") {
      result = result.filter((c) => !c.active)
    }

    if (sortBy === "discount_percent_desc") {
      result.sort((a, b) => b.discount_percent - a.discount_percent)
    } else if (sortBy === "discount_percent_asc") {
      result.sort((a, b) => a.discount_percent - b.discount_percent)
    } else if (sortBy === "created_at_asc") {
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    } else {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }

    return result
  }, [discountCodes, searchQuery, statusFilter, sortBy])

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      showSnackbar("Copied to clipboard")
    } catch {
      showSnackbar("Failed to copy to clipboard", "error")
    }
  }

  const resetCreateForm = () => {
    setCreateForm({ discount_percent: "", code: "" })
    setFormError("")
    setPercentTouched(false)
  }

  const openCreateModal = () => {
    resetCreateForm()
    setCreateOpen(true)
  }

  const getCreatePreviewCode = () => {
    const customCode = createForm.code.trim()
    if (customCode) return customCode
    if (createForm.discount_percent) return `SAVE${createForm.discount_percent}`
    return null
  }

  const getCreatePercentError = () => {
    if (!percentTouched || !createForm.discount_percent) return ""
    return validatePercent(createForm.discount_percent)
      ? ""
      : "Enter a value between 1 and 100."
  }

  const handleCreateSubmit = async () => {
    setPercentTouched(true)

    if (!validatePercent(createForm.discount_percent)) {
      setFormError("Enter a value between 1 and 100.")
      return
    }

    const promoCode = createForm.code.trim()
    if (promoCode && !PROMO_CODE_PATTERN.test(promoCode)) {
      setFormError(
        "Promo codes can only contain letters, numbers, hyphens (-), and underscores (_). Spaces and special characters are not allowed.",
      )
      return
    }

    setSubmitting(true)
    setFormError("")
    try {
      await onCreate({
        discount_percent: parseInt(createForm.discount_percent, 10),
        code: promoCode || undefined,
      })
      setCreateOpen(false)
      resetCreateForm()
      showSnackbar("Discount code created successfully")
    } catch (err) {
      const message = formatApiError(
        err.response?.data?.error || err.message || "Failed to create discount code",
      )
      setFormError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const getEditPercentError = () => {
    if (!editPercentTouched || !editForm.discount_percent) return ""
    return validatePercent(editForm.discount_percent)
      ? ""
      : "Enter a value between 1 and 100."
  }

  const openEditDialog = (code) => {
    setSelectedCode(code)
    setEditForm({
      discount_percent: String(code.discount_percent),
      active: code.active,
    })
    setFormError("")
    setEditPercentTouched(false)
    setEditOpen(true)
  }

  const handleEditSubmit = async () => {
    setEditPercentTouched(true)

    if (!validatePercent(editForm.discount_percent)) {
      setFormError("Enter a value between 1 and 100.")
      return
    }

    setSubmitting(true)
    setFormError("")
    try {
      await onUpdate(selectedCode.id, {
        discount_percent: parseInt(editForm.discount_percent, 10),
        active: editForm.active,
      })
      setEditOpen(false)
      setSelectedCode(null)
      showSnackbar("Discount code updated successfully")
    } catch (err) {
      const message = formatApiError(
        err.response?.data?.error || err.message || "Failed to update discount code",
      )
      setFormError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const openDeleteDialog = (code) => {
    setSelectedCode(code)
    setDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    setSubmitting(true)
    try {
      await onDelete(selectedCode.id)
      setDeleteOpen(false)
      setSelectedCode(null)
      showSnackbar("Discount code deleted successfully")
    } catch (err) {
      const message = formatApiError(
        err.response?.data?.error || err.message || "Failed to delete discount code",
      )
      showSnackbar(message, "error")
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleActive = async (code) => {
    try {
      await onUpdate(code.id, { active: !code.active })
      showSnackbar(`Discount code ${code.active ? "deactivated" : "activated"} successfully`)
    } catch (err) {
      const message = formatApiError(
        err.response?.data?.error || err.message || "Failed to update status",
      )
      showSnackbar(message, "error")
    }
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    )
  }

  const hasNoCodes = discountCodes.length === 0
  const hasNoResults = !hasNoCodes && filteredCodes.length === 0

  const renderCodeActions = (code) => (
    <Box className={styles.actionGroup}>
      <Tooltip title="Edit">
        <IconButton
          size="small"
          className={styles.iconAction}
          onClick={() => openEditDialog(code)}
          aria-label={`Edit ${code.code}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={code.active ? "Deactivate" : "Activate"}>
        <IconButton
          size="small"
          className={styles.iconAction}
          onClick={() => handleToggleActive(code)}
          aria-label={code.active ? `Deactivate ${code.code}` : `Activate ${code.code}`}
        >
          {code.active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          className={`${styles.iconAction} ${styles.iconActionDanger}`}
          onClick={() => openDeleteDialog(code)}
          aria-label={`Delete ${code.code}`}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <button
          type="button"
          className={styles.backLink}
          onClick={() => history.push(paths.home)}
        >
          <ArrowBackIcon fontSize="small" />
          Back to Dashboard
        </button>

        <Box className={styles.pageHeader}>
          <Box className={styles.pageHeaderText}>
            <Typography variant="h4" className={styles.title}>
              Stripe Discount Codes
            </Typography>
            <Typography variant="body1" className={styles.subtitle}>
              Create and manage shared promotion codes that customers can redeem during checkout.
            </Typography>
          </Box>
          <Button
            variant="contained"
            className={styles.createButton}
            onClick={openCreateModal}
            startIcon={<AddIcon />}
          >
            Create Discount Code
          </Button>
        </Box>

        {error && (
          <Typography variant="body1" className={styles.error}>
            {error}
          </Typography>
        )}

        {!hasNoCodes && (
          <Box className={styles.toolbar}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search by promo code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={styles.searchIcon} />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Search promo codes" }}
            />
            <FormControl variant="outlined" size="small" className={styles.filterControl}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" className={styles.filterControl}>
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort by"
              >
                <MenuItem value="created_at_desc">Newest first</MenuItem>
                <MenuItem value="created_at_asc">Oldest first</MenuItem>
                <MenuItem value="discount_percent_desc">Discount: High to low</MenuItem>
                <MenuItem value="discount_percent_asc">Discount: Low to high</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {hasNoCodes ? (
          <Box className={styles.emptyStateCard}>
            <LocalOfferIcon className={styles.emptyStateIcon} />
            <Typography variant="h6" className={styles.emptyStateTitle}>
              No discount codes yet
            </Typography>
            <Typography variant="body2" className={styles.emptyStateText}>
              Create your first promotion code to start offering discounts to customers.
            </Typography>
            <Button
              variant="contained"
              className={styles.createButton}
              onClick={openCreateModal}
              startIcon={<AddIcon />}
            >
              Create Discount Code
            </Button>
          </Box>
        ) : (
          <>
            <div className={styles.desktopTable}>
              <TableContainer component={Paper} className={styles.tableContainer} elevation={0}>
                <Table className={styles.table} stickyHeader>
                  <TableHead>
                    <TableRow className={styles.tableHeadRow}>
                      <TableCell className={styles.tableHeadCell}>Promo Code</TableCell>
                      <TableCell className={`${styles.tableHeadCell} ${styles.discountCol}`} align="center">
                        Discount
                      </TableCell>
                      <TableCell className={styles.tableHeadCell}>Status</TableCell>
                      <TableCell className={`${styles.tableHeadCell} ${styles.actionsCol}`} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hasNoResults ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" className={styles.noResults}>
                            No codes match your search or filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCodes.map((code) => (
                        <TableRow key={code.id} className={styles.tableRow}>
                          <TableCell className={styles.tableCell}>
                            <Box className={styles.codeCell}>
                              <span className={styles.promoBadge}>{code.code}</span>
                              <Tooltip title="Copy promo code">
                                <IconButton
                                  size="small"
                                  className={styles.iconAction}
                                  onClick={() => handleCopy(code.code)}
                                  aria-label={`Copy ${code.code}`}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell className={`${styles.tableCell} ${styles.discountCol}`} align="center">
                            <span className={styles.discountBadge}>{code.discount_percent}%</span>
                          </TableCell>
                          <TableCell className={styles.tableCell}>
                            <span
                              className={`${styles.statusPill} ${
                                code.active ? styles.statusActive : styles.statusInactive
                              }`}
                            >
                              {code.status}
                            </span>
                          </TableCell>
                          <TableCell className={`${styles.tableCell} ${styles.actionsCol}`} align="right">
                            {renderCodeActions(code)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <div className={styles.mobileList}>
              {hasNoResults ? (
                <Box className={styles.mobileCard}>
                  <Typography variant="body2" className={styles.noResults}>
                    No codes match your search or filters.
                  </Typography>
                </Box>
              ) : (
                filteredCodes.map((code) => (
                  <Box key={code.id} className={styles.mobileCard}>
                    <Box className={styles.mobileCardHeader}>
                      <Box className={styles.codeCell}>
                        <span className={styles.promoBadge}>{code.code}</span>
                        <Tooltip title="Copy promo code">
                          <IconButton
                            size="small"
                            className={styles.iconAction}
                            onClick={() => handleCopy(code.code)}
                            aria-label={`Copy ${code.code}`}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <span
                        className={`${styles.statusPill} ${
                          code.active ? styles.statusActive : styles.statusInactive
                        }`}
                      >
                        {code.status}
                      </span>
                    </Box>
                    <Box className={styles.mobileCardFooter}>
                      <span className={styles.discountBadge}>{code.discount_percent}% off</span>
                      {renderCodeActions(code)}
                    </Box>
                  </Box>
                ))
              )}
            </div>
          </>
        )}

        {/* Create Dialog */}
        <Dialog
          open={createOpen}
          onClose={() => !submitting && setCreateOpen(false)}
          maxWidth={false}
          classes={{ paper: styles.createDialogPaper }}
          aria-labelledby="create-discount-code-title"
        >
          <DialogTitle disableTypography className={styles.createDialogTitle}>
            <Typography id="create-discount-code-title" variant="h6" className={styles.createDialogHeading}>
              Create Stripe Discount Code
            </Typography>
            <Typography variant="body2" className={styles.createDialogSubtitle}>
              Create a promotion code customers can redeem during checkout.
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.createDialogContent}>
            <Box className={styles.formFieldGroup}>
              <Typography component="label" htmlFor="create-discount-percent" className={styles.fieldLabel}>
                Discount Percentage
              </Typography>
              <TextField
                id="create-discount-percent"
                autoFocus
                variant="outlined"
                size="small"
                type="text"
                inputMode="numeric"
                fullWidth
                placeholder="20"
                value={createForm.discount_percent}
                onChange={(e) => {
                  setPercentTouched(true)
                  setCreateForm({
                    ...createForm,
                    discount_percent: parsePercentInput(e.target.value),
                  })
                  if (formError) setFormError("")
                }}
                onBlur={() => setPercentTouched(true)}
                error={Boolean(getCreatePercentError())}
                helperText={getCreatePercentError() || "Enter a value between 1 and 100."}
                className={styles.createDialogInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className={styles.percentSuffix}>
                      %
                    </InputAdornment>
                  ),
                }}
                inputProps={{ "aria-label": "Discount percentage" }}
              />
            </Box>

            <Box className={styles.formDivider} aria-hidden="true" />

            <Box className={styles.formFieldGroup}>
              <Box className={styles.labelRow}>
                <Typography component="label" htmlFor="create-promo-code" className={styles.fieldLabel}>
                  Promo Code
                </Typography>
                <span className={styles.optionalBadge}>Optional</span>
              </Box>
              <TextField
                id="create-promo-code"
                variant="outlined"
                size="small"
                fullWidth
                placeholder="SAVE20"
                value={createForm.code}
                onChange={(e) =>
                  setCreateForm({ ...createForm, code: sanitizePromoCode(e.target.value) })
                }
                className={styles.createDialogInput}
                inputProps={{ "aria-label": "Custom promo code" }}
              />
              <Typography variant="caption" className={styles.inlinePreviewHint}>
                Use letters, numbers, hyphens, or underscores only (e.g. SAVE20).
              </Typography>
              <Typography variant="body2" className={styles.inlinePreview} aria-live="polite">
                {getCreatePreviewCode() ? (
                  <>
                    Preview: <strong className={styles.previewCode}>{getCreatePreviewCode()}</strong>
                  </>
                ) : (
                  "An auto-generated code will be created."
                )}
              </Typography>
              <Typography variant="caption" className={styles.inlinePreviewHint}>
                If already taken, a numeric suffix will be added automatically.
              </Typography>
            </Box>

            {formError && (
              <Typography role="alert" color="error" variant="body2" className={styles.formError}>
                {formError}
              </Typography>
            )}
          </DialogContent>

          <DialogActions className={styles.createDialogActions}>
            <Button
              onClick={() => setCreateOpen(false)}
              disabled={submitting}
              className={styles.dialogCancelButton}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={submitting}
              className={styles.dialogPrimaryButton}
              variant="contained"
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editOpen}
          onClose={() => !submitting && setEditOpen(false)}
          maxWidth={false}
          classes={{ paper: styles.createDialogPaper }}
          aria-labelledby="edit-discount-code-title"
        >
          <DialogTitle disableTypography className={styles.createDialogTitle}>
            <Typography id="edit-discount-code-title" variant="h6" className={styles.createDialogHeading}>
              Edit Discount Code
            </Typography>
            <Typography variant="body2" className={styles.createDialogSubtitle}>
              Update the discount percentage or activation status for this promotion code.
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.createDialogContent}>
            <Box className={styles.formFieldGroup}>
              <Typography className={styles.fieldLabel}>Promo Code</Typography>
              <span className={styles.editPromoBadge}>{selectedCode?.code}</span>
              <Typography variant="caption" className={styles.inlinePreviewHint}>
                Promo codes cannot be changed after creation.
              </Typography>
            </Box>

            <Box className={styles.formDivider} aria-hidden="true" />

            <Box className={styles.formFieldGroup}>
              <Typography component="label" htmlFor="edit-discount-percent" className={styles.fieldLabel}>
                Discount Percentage
              </Typography>
              <TextField
                id="edit-discount-percent"
                autoFocus
                variant="outlined"
                size="small"
                type="text"
                inputMode="numeric"
                fullWidth
                placeholder="20"
                value={editForm.discount_percent}
                onChange={(e) => {
                  setEditPercentTouched(true)
                  setEditForm({
                    ...editForm,
                    discount_percent: parsePercentInput(e.target.value),
                  })
                  if (formError) setFormError("")
                }}
                onBlur={() => setEditPercentTouched(true)}
                error={Boolean(getEditPercentError())}
                helperText={
                  getEditPercentError() || "Changing the percentage creates a new Stripe coupon."
                }
                className={styles.createDialogInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className={styles.percentSuffix}>
                      %
                    </InputAdornment>
                  ),
                }}
                inputProps={{ "aria-label": "Discount percentage" }}
              />
            </Box>

            <Box className={styles.formDivider} aria-hidden="true" />

            <Box className={styles.formFieldGroup}>
              <Typography className={styles.fieldLabel}>Status</Typography>
              <Box className={styles.editActiveRow}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.active}
                      onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={editForm.active ? "Active" : "Inactive"}
                  className={styles.editActiveSwitch}
                />
                <span
                  className={`${styles.statusPill} ${
                    editForm.active ? styles.statusActive : styles.statusInactive
                  }`}
                >
                  {editForm.active ? "Active" : "Inactive"}
                </span>
              </Box>
              <Typography variant="caption" className={styles.inlinePreviewHint}>
                Inactive codes cannot be redeemed at checkout.
              </Typography>
            </Box>

            {formError && (
              <Typography role="alert" color="error" variant="body2" className={styles.formError}>
                {formError}
              </Typography>
            )}
          </DialogContent>

          <DialogActions className={styles.createDialogActions}>
            <Button
              onClick={() => setEditOpen(false)}
              disabled={submitting}
              className={styles.dialogCancelButton}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={submitting}
              className={styles.dialogPrimaryButton}
              variant="contained"
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog
          open={deleteOpen}
          onClose={() => !submitting && setDeleteOpen(false)}
          maxWidth={false}
          classes={{ paper: styles.deleteDialogPaper }}
          aria-labelledby="delete-discount-code-title"
        >
          <DialogTitle disableTypography className={styles.deleteDialogTitle}>
            <Typography id="delete-discount-code-title" variant="h6" className={styles.deleteDialogHeading}>
              Delete discount code?
            </Typography>
          </DialogTitle>

          <DialogContent className={styles.deleteDialogContent}>
            <Typography variant="body2" className={styles.deleteDialogBody}>
              <span className={styles.deleteDialogCode}>{selectedCode?.code}</span> will be removed
              and can no longer be used at checkout.
            </Typography>
          </DialogContent>

          <DialogActions className={styles.deleteDialogActions}>
            <Button
              onClick={() => setDeleteOpen(false)}
              disabled={submitting}
              className={styles.dialogCancelButton}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={submitting}
              className={styles.dialogDangerButton}
              variant="contained"
            >
              {submitting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  )
}

AdminStripeDiscountCodeListContainer.propTypes = {
  discountCodes: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default AdminStripeDiscountCodeListContainer
